// client/src/components/influencer/project/modals/ResultSubmitConfirmModal.js
import React, { useState, useEffect } from 'react';

function ResultSubmitConfirmModal({ open, onClose, onBack, onSubmitSuccess, projectId, urls, files, note, submitEnd }) {
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 모달이 열릴 때 배경 스크롤 막기
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const uid = window.__ME__?.id;

  // ✅ 이미지 업로드 함수
  const uploadImages = async () => {
    if (!files || files.length === 0) return [];

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('projectId', projectId);
    formData.append('ownerId', uid);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // 진행률 추적
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.ok && data.uploadedFiles) {
              resolve(data.uploadedFiles.map(f => f.key));
            } else {
              reject(new Error(data.message || '업로드 실패'));
            }
          } catch (e) {
            reject(new Error('응답 파싱 실패'));
          }
        } else {
          try {
            const data = JSON.parse(xhr.responseText);
            reject(new Error(data.message || '업로드 실패'));
          } catch (e) {
            reject(new Error('업로드 실패'));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('네트워크 오류가 발생했습니다.'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('업로드가 취소되었습니다.'));
      });

      xhr.open('POST', '/api/influencer/mybiz/projects/result-upload');
      xhr.withCredentials = true;
      xhr.send(formData);
    });
  };

  const handleConfirm = async () => {
    if (submitEnd && new Date(submitEnd) < new Date()) {
      alert(
        '⚠️ 결과물 제출 마감일이 지났습니다.\n\n' +
        '마감일 이후에는 제출이 불가합니다.\n' +
        '특수한 사정이 있는 경우 아래로 문의해 주세요.\n\n' +
        '📧 문의: support@selfad.co.kr'
      );
      return;
    }

    setSubmitting(true);
    setUploadProgress(0);

    try {
      // ✅ 1. 이미지 업로드 (있으면)
      let uploadedImages = [];
      if (files && files.length > 0) {
        uploadedImages = await uploadImages();
      }

      // ✅ 2. DB 저장
      const res = await fetch(`/api/influencer/mybiz/projects/${projectId}/submit-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          urls: urls || [],
          images: uploadedImages,
          note: note || ''
        })
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        const err = new Error(data.message || '제출 실패');
        err.code = data.code;
        throw err;
      }

      // 성공 처리
      onSubmitSuccess();
      onClose();

    } catch (error) {
      const isDeadlineError =
        error.code === 'RESULT_SUBMIT_DEADLINE_PASSED' ||
        error.message?.includes('마감일이 지났');

      if (isDeadlineError) {
        alert(
          '⚠️ 결과물 제출 마감일이 지났습니다.\n\n' +
          '마감일 이후에는 시스템에서 제출이 차단됩니다.\n' +
          '특수한 사정이 있는 경우 아래로 문의해 주세요.\n\n' +
          '📧 문의: support@selfad.co.kr'
        );
      } else {
        alert('제출 실패: ' + error.message);
      }
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="influencer-result-modal">
      <div className="influencer-result-modal__content" style={{ maxWidth: 500, position: 'relative' }}>
        {/* X 닫기 버튼 */}
        <button 
          onClick={submitting ? null : onClose}
          disabled={submitting}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            padding: '0',
            margin: '0',
            background: 'transparent',
            border: 'none',
            cursor: submitting ? 'not-allowed' : 'pointer',
            fontSize: '26px',
            color: submitting ? '#d1d5db' : '#9ca3af',
            lineHeight: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s',
            zIndex: 10
          }}
          onMouseEnter={(e) => !submitting && (e.target.style.color = '#374151')}
          onMouseLeave={(e) => !submitting && (e.target.style.color = '#9ca3af')}
        >
          <span style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}>×</span>
        </button>

        <div className="result-submit-confirm">
          {/* 아이콘 */}
          <div className="result-submit-confirm__icon">⚠️</div>

          {/* 제목 */}
          <h2 className="result-submit-confirm__title">정말 제출하시겠습니까?</h2>

          {/* 메시지 */}
          <div className="result-submit-confirm__message">
            <p>제출 후 <strong>수정이 불가능</strong>합니다.</p>
            <p>
              내용이 부족하면 추후에 결과물 증빙을 재요청할 수 있습니다.
            </p>
          </div>

          {/* 제출 내용 요약 */}
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>제출 내용</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
              <span style={{ color: '#6b7280' }}>콘텐츠 URL:</span>
              <span style={{ color: '#111827', fontWeight: 600 }}>{urls?.length || 0}개</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
              <span style={{ color: '#6b7280' }}>첨부 이미지:</span>
              <span style={{ color: '#111827', fontWeight: 600 }}>{files?.length || 0}개</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
              <span style={{ color: '#6b7280' }}>기타 사항:</span>
              <span style={{ color: '#111827', fontWeight: 600 }}>{note ? '작성됨' : '없음'}</span>
            </div>
          </div>

          {/* ✅ 업로드 중 표시 */}
          {submitting && (
            <div style={{ marginTop: 16, marginBottom: 16, padding: 16, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1e40af', marginBottom: 12 }}>
                {uploadProgress > 0 && uploadProgress < 100 
                  ? `이미지 업로드 중... ${uploadProgress}%`
                  : '결과물 제출 중입니다...'}
              </div>
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div style={{ width: '100%', height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
                  <div style={{ height: '100%', background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)', transition: 'width 0.3s ease', width: `${uploadProgress}%` }} />
                </div>
              )}
              <div style={{ margin: '0 auto', width: 24, height: 24, border: '3px solid #bfdbfe', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            </div>
          )}

          {/* 버튼 */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              className="influencer-result-modal__button influencer-result-modal__button--cancel"
              style={{ flex: 1 }}
              onClick={onBack}
              disabled={submitting}
            >
              뒤로
            </button>
            <button
              className="influencer-result-modal__button influencer-result-modal__button--submit"
              style={{ flex: 1 }}
              onClick={handleConfirm}
              disabled={submitting}
            >
              {submitting ? '제출 중...' : '제출'}
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default ResultSubmitConfirmModal;

